import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

interface AdminData {
  role: string;
  isActive: boolean;
  permissions?: string[];
}

// Cache admin status to avoid repeated Firestore calls
const adminStatusCache: Map<string, { isAdmin: boolean; timestamp: number }> = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const isUserAdmin = async (userId: string): Promise<boolean> => {
  try {
    // Check cache first
    const cached = adminStatusCache.get(userId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.isAdmin;
    }

    // Check admin document in Firestore
    const adminDocRef = doc(db, 'admins', userId);
    const adminDoc = await getDoc(adminDocRef);

    if (!adminDoc.exists()) {
      adminStatusCache.set(userId, { isAdmin: false, timestamp: Date.now() });
      return false;
    }

    const adminData = adminDoc.data() as AdminData;
    const isAdmin = adminData.isActive !== false && adminData.role === 'admin';

    // Update cache
    adminStatusCache.set(userId, { isAdmin, timestamp: Date.now() });

    return isAdmin;
  } catch (error) {
    console.error('Failed to verify admin status:', error);
    // Default to false on error for security
    return false;
  }
};

export const getAdminPermissions = async (userId: string): Promise<string[]> => {
  try {
    const adminDocRef = doc(db, 'admins', userId);
    const adminDoc = await getDoc(adminDocRef);

    if (!adminDoc.exists()) {
      return [];
    }

    const adminData = adminDoc.data() as AdminData;
    return adminData.permissions || ['analytics', 'reports'];
  } catch (error) {
    console.error('Failed to get admin permissions:', error);
    return [];
  }
};

export const hasPermission = async (userId: string, permission: string): Promise<boolean> => {
  const permissions = await getAdminPermissions(userId);
  return permissions.includes(permission);
};

export const subscribeToAdminStatus = (
  userId: string,
  callback: (isAdmin: boolean) => void
): (() => void) => {
  const adminDocRef = doc(db, 'admins', userId);
  
  return onSnapshot(adminDocRef, (snapshot) => {
    if (!snapshot.exists()) {
      callback(false);
      return;
    }

    const adminData = snapshot.data() as AdminData;
    const isAdmin = adminData.isActive !== false && adminData.role === 'admin';
    
    // Update cache
    adminStatusCache.set(userId, { isAdmin, timestamp: Date.now() });
    
    callback(isAdmin);
  }, (error) => {
    console.error('Error listening to admin status:', error);
    callback(false);
  });
};

export const clearAdminCache = (userId?: string) => {
  if (userId) {
    adminStatusCache.delete(userId);
  } else {
    adminStatusCache.clear();
  }
};

